import React, { useState, useEffect } from 'react';

import { Grid } from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { trackEvent } from 'on_web/helpers/userTrackingEventsHelper';

import GeneralIntro from 'on_web/components/onboarding/general/GeneralIntro';
import GeneralStep1 from 'on_web/components/onboarding/general/GeneralStep1';
import GeneralStep2 from 'on_web/components/onboarding/general/GeneralStep2';
import GeneralStep3 from 'on_web/components/onboarding/general/GeneralStep3';
import PageItemContainer from 'on_web/components/shared/Common/PageItemContainer/PageItemContainer';

import {
  ROUTE_ONBOARDING_DNA,
  ROUTE_ONBOARDING_LIFESTYLE,
  ROUTE_ONBOARDING_DISEASES,
  ROUTE_ONBOARDING_INTRO,
  ROUTE_ONBOARDING_DNA_INSTRUCTIONS,
  ROUTE_HEALTH_PROFILE,
} from 'routes';
import { getRedirectRoute } from '../helpers/redirectHelper';

import useUsersApi from '../hooks/useUsersApi';
import UserProfileContext from 'on_web/context/userProfileContext';

function OnboardingGeneral(props) {
  const context = React.useContext(UserProfileContext);
  const [visibleStep, setVisibleStep] = useState(
    props.location.state ? null : 0
  );
  const [editMode, setEditMode] = useState(false);
  const [
    {
      response,
      loading,
      responseProfile,
      loadingProfile,
      getUserData,
      saveUserData,
      getProfile,
      saveProfile,
    },
  ] = useUsersApi();

  useEffect(() => {
    let userData;
    getUserData()
      .then(userDataResponse => {
        userData = userDataResponse;
        return getProfile();
      })
      .then(profileResponse => {
        if (userData != null && profileResponse != null) {
          if (props.location.state && props.location.state.editMode) {
            // if edit mode is requested, jump to requested step
            setEditMode(true);
            setVisibleStep(props.location.state.editStep);
            return;
          } else {
            setEditMode(false);
          }

          if (
            visibleStep == null &&
            props.location.state &&
            props.location.state.startFromLast
          ) {
            setVisibleStep(3);
          } else if (Object.keys(userData.user_metadata).length === 0) {
            props.history.push(ROUTE_ONBOARDING_INTRO);
          } else if (
            profileResponse.gender == null ||
            profileResponse.year_of_birth == null ||
            userData.user_metadata.firstName == null
          ) {
            setVisibleStep(0);
          } else if (
            visibleStep == null &&
            props.location.state &&
            props.location.state.startFromFirst
          ) {
            setVisibleStep(1);
          } else if (
            profileResponse.height == null ||
            profileResponse.weight == null
          ) {
            setVisibleStep(2);
          } else if (userData.user_metadata.generalEthnicity == null) {
            setVisibleStep(3);
          } else {
            const route = getRedirectRoute(
              userData.user_metadata,
              context.showBasicQuestions
            );
            props.history.push(route);
          }
        }
      });
  }, [getUserData, getProfile]);

  const handleOnWelcome = () => {
    goToNextStep();
  };

  const screenShouldBeVisible = screenIndex => {
    return visibleStep === screenIndex;
  };

  const goToNextStep = async (userMetaData, userProfile) => {
    if (userProfile) {
      saveProfile(userProfile).then(resp => {
        if (resp != null) {
          next(userMetaData);
        }
      });
    } else {
      next(userMetaData);
    }
  };

  const next = userMetaData => {
    if (visibleStep + 1 <= 3) {
      if (userMetaData) {
        response.user_metadata = userMetaData;
        saveUserData(response).then(resp => {
          if (resp != null) {
            if (editMode) {
              props.history.push(ROUTE_HEALTH_PROFILE);
            } else {
              setVisibleStep(visibleStep + 1);
            }
          }
        });
      } else {
        if (editMode) {
          props.history.push(ROUTE_HEALTH_PROFILE);
        } else {
          setVisibleStep(visibleStep + 1);
        }
      }
    } else {
      response.user_metadata = userMetaData;
      response.user_metadata.onboarding = {
        ...response.user_metadata.onboarding,
        generalFinished: true,
      };
      saveUserData(response).then(resp => {
        if (resp != null) {
          trackEvent(
            'Completed GENERAL',
            'User has completed GENERAL section onboarding'
          );

          if (editMode) {
            props.history.push(ROUTE_HEALTH_PROFILE);
          } else {
            props.history.push({
              pathname: ROUTE_ONBOARDING_LIFESTYLE,
              state: { startFromFirst: true },
            });
          }
        }
      });
    }
  };

  const handleStep1BackClick = () => {
    /*
    let userMetaData = response.user_metadata;
    let route =
      userMetaData.dnaUsageOption && userMetaData.dnaUsageOption === '2'
        ? ROUTE_ONBOARDING_DNA_INSTRUCTIONS
        : ROUTE_ONBOARDING_DNA;*/

    props.history.push({
      pathname: ROUTE_ONBOARDING_INTRO,
      state: {
        startFromLast: true,
      },
    });
  };

  const goToPreviousStep = () => {
    setVisibleStep(visibleStep - 1);
  };

  return (
    <Grid
      container={useMediaQuery('(min-width:1000px)')}
      direction="row"
      justify="center"
      alignItems="center"
    >
      {loading === false &&
        response &&
        visibleStep != null &&
        loadingProfile === false &&
        responseProfile && (
          <UserProfileContext.Provider
            value={{
              userMetaData: response,
              userProfile: responseProfile,
              showBasicQuestions:
                context && context.showBasicQuestions === true
                  ? context.showBasicQuestions
                  : false,
              editMode: editMode,
            }}
          >
            {screenShouldBeVisible(0) && (
              <PageItemContainer>
                <GeneralIntro onWelcome={handleOnWelcome} />
              </PageItemContainer>
            )}
            {screenShouldBeVisible(1) && (
              <PageItemContainer>
                <GeneralStep1
                  onClickBack={() => handleStep1BackClick()}
                  onClickNext={(userMetadata, userProfile) =>
                    goToNextStep(userMetadata, userProfile)
                  }
                />
              </PageItemContainer>
            )}
            {screenShouldBeVisible(2) && (
              <PageItemContainer>
                <GeneralStep2
                  onClickBack={goToPreviousStep}
                  onClickNext={(userMetadata, userProfile) => {
                    goToNextStep(userMetadata, userProfile);
                  }}
                />
              </PageItemContainer>
            )}
            {screenShouldBeVisible(3) && (
              <PageItemContainer>
                <GeneralStep3
                  onClickBack={goToPreviousStep}
                  onClickNext={userMetadata => goToNextStep(userMetadata)}
                />
              </PageItemContainer>
            )}
          </UserProfileContext.Provider>
        )}
    </Grid>
  );
}

export default OnboardingGeneral;
